import { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchPurchaseOrderArrivalScheduleList } from '#/api/srm/purchaseOrder/fetchArrivalScheduleList';
import useDetailTable from '#/hooks/table/useDetailTable';
import {
  purchaseOrderArrivalScheduleListQueryKey,
  purchaseOrderArrivalScheduleListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

import DetailTable from '#/modules/commonComponents/table/DetailTable';
import { buildPurchaseOrderArrivalScheduleColumns } from '#/modules/srmMgmt/purchaseOrder/data/purchaseOrderArrivalScheduleColumns';
import { getPurchaseOrderApiErrorMessage } from '#/modules/srmMgmt/purchaseOrder/helper/apiError';

const PurchaseOrderArrivalScheduleSection = ({ purchaseOrderId }) => {
  const queryClient = useQueryClient();

  const handleMutated = useCallback(async () => {
    if (!purchaseOrderId) {
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: purchaseOrderArrivalScheduleListQueryKeyPrefix(purchaseOrderId),
    });
  }, [purchaseOrderId, queryClient]);

  const columns = useMemo(
    () => buildPurchaseOrderArrivalScheduleColumns({
      purchaseOrderId,
      onMutated: handleMutated,
    }),
    [purchaseOrderId, handleMutated],
  );

  const listQuery = useQuery({
    queryKey: purchaseOrderArrivalScheduleListQueryKey(purchaseOrderId, {}),
    queryFn: () => fetchPurchaseOrderArrivalScheduleList(purchaseOrderId, {}),
    enabled: Boolean(purchaseOrderId),
  });

  useEffect(() => {
    if (!listQuery.isError) {
      return;
    }
    toast.error(getPurchaseOrderApiErrorMessage(listQuery.error, '載入到貨排程失敗'));
  }, [listQuery.isError, listQuery.error]);

  const rows = useMemo(
    () => (listQuery.data?.data ?? []).map((row, index) => ({
      ...row,
      _rowKey: row?.id != null && row.id !== ''
        ? `${row.id}-${index}`
        : `arrival-schedule-${index}`,
    })),
    [listQuery.data?.data],
  );

  const detailTableState = useDetailTable(rows, columns, {
    enableExpanding: false,
    getRowId: (row) => String(row._rowKey),
  });

  if (listQuery.isLoading) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">載入中…</p>;
  }

  if (listQuery.isError) {
    return (
      <p className="px-4 py-5 text-sm text-destructive">
        {getPurchaseOrderApiErrorMessage(listQuery.error, '載入到貨排程失敗')}
      </p>
    );
  }

  if (rows.length === 0) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">尚無資料</p>;
  }

  return (
    <div className="detail-table-container">
      <DetailTable
        state={detailTableState}
        containerClassName="detail-table-scroll--section-embedded"
        tableClassName="detail-table--flat-lines"
      />
    </div>
  );
};

PurchaseOrderArrivalScheduleSection.propTypes = {
  purchaseOrderId: PropTypes.string,
};

export default PurchaseOrderArrivalScheduleSection;
